import React, { useState } from 'react';
import { ShoppingBag, FileText, Receipt, CreditCard, Landmark, ChevronRight, CheckCircle2, Copy } from 'lucide-react';
import { toast } from 'sonner';

export const idr = (n) => 'Rp ' + Number(n || 0).toLocaleString('id-ID');
const lineTotal = (l) => Number(l.qty || 0) * Number(l.price || 0) * (1 - Number(l.discount || 0) / 100);

export const ProductThumb = ({ p, className = 'h-24' }) => p.image_url
  ? <img src={p.image_url} alt={p.name} className={`${className} w-full object-cover`} loading="lazy" />
  : <div className={`${className} w-full flex items-center justify-center bg-[rgba(27,42,107,0.06)] text-[hsl(var(--pillot-navy))] font-display font-semibold text-xl`}>{(p.name || '?').slice(0, 2).toUpperCase()}</div>;

/** WhatsApp-Business-style product catalog card. */
export const CatalogCard = ({ meta, onBrowse, compact = false }) => {
  const products = meta?.products || [];
  const shown = products.slice(0, compact ? 2 : 3);
  return (
    <div className="w-[260px] rounded-2xl border border-border bg-white overflow-hidden" data-testid="card-catalog">
      <div className="px-3 py-2 flex items-center gap-2 text-xs font-medium border-b border-border bg-muted/40"><ShoppingBag size={13} className="text-primary" />Katalog · {products.length} produk</div>
      <div className="divide-y divide-border">
        {shown.map((p) => (
          <div key={p.id} className="flex gap-3 p-2.5" data-testid={`catalog-item-${p.id}`}>
            <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl"><ProductThumb p={p} className="h-14" /></div>
            <div className="min-w-0 flex-1"><div className="truncate text-sm font-medium">{p.name}</div><div className="text-xs text-muted-foreground line-clamp-1">{p.description}</div><div className="text-sm font-semibold text-primary mt-0.5">{idr(p.price)}</div></div>
          </div>
        ))}
      </div>
      <button type="button" onClick={() => onBrowse?.(products)} className="w-full px-3 py-2 text-sm font-medium text-primary hover:bg-muted/60 flex items-center justify-center gap-1" data-testid="catalog-browse">Lihat semua produk <ChevronRight size={14} /></button>
    </div>
  );
};

const Lines = ({ lines }) => (
  <div className="divide-y divide-border text-xs">
    {(lines || []).map((l, i) => <div key={i} className="flex justify-between gap-2 py-1.5"><span className="min-w-0 truncate">{l.name} <span className="text-muted-foreground">×{Number(l.qty)}</span></span><span className="tabular-nums whitespace-nowrap">{idr(lineTotal(l))}</span></div>)}
  </div>
);

const Totals = ({ m }) => (
  <div className="mt-2 space-y-0.5 text-xs">
    <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span className="tabular-nums">{idr(m.subtotal)}</span></div>
    {m.tax > 0 && <div className="flex justify-between text-muted-foreground"><span>PPN</span><span className="tabular-nums">{idr(m.tax)}</span></div>}
    <div className="flex justify-between font-semibold text-sm pt-1 border-t border-border"><span>Total</span><span className="tabular-nums" data-testid="card-total">{idr(m.total)}</span></div>
  </div>
);

export const QuotationCard = ({ meta }) => (
  <div className="w-[270px] rounded-2xl border border-border bg-white p-3" data-testid="card-quotation">
    <div className="flex items-center gap-2 text-xs font-medium mb-2"><FileText size={13} className="text-primary" />Penawaran {meta.number}<span className="ml-auto text-[11px] text-muted-foreground">{meta.date}</span></div>
    <Lines lines={meta.lines} /><Totals m={meta} />
    {meta.notes && <p className="mt-2 text-[11px] text-muted-foreground whitespace-pre-wrap">{meta.notes}</p>}
  </div>
);

const copy = (t) => { navigator.clipboard?.writeText(t); toast.success('Disalin'); };

export const InvoiceCard = ({ meta, onPay, paying, paid }) => {
  const [showBank, setShowBank] = useState(false);
  const due = Math.max(0, Number(meta.total) - Number(meta.paid || 0));
  const isPaid = paid || meta.state === 'paid' || due === 0;
  return (
    <div className="w-[280px] rounded-2xl border border-border bg-white p-3" data-testid="card-invoice">
      <div className="flex items-center gap-2 text-xs font-medium mb-2"><Receipt size={13} className="text-primary" />Invoice {meta.number}{meta.due_date && <span className="ml-auto text-[11px] text-muted-foreground">Jatuh tempo {meta.due_date}</span>}</div>
      <Lines lines={meta.lines} /><Totals m={meta} />
      {isPaid ? <div className="mt-3 flex items-center gap-1.5 rounded-xl bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-700" data-testid="invoice-paid-badge"><CheckCircle2 size={14} />Lunas</div> : (
        <div className="mt-3 space-y-2">
          {meta.xendit_enabled && onPay && <button type="button" disabled={paying} onClick={onPay} className="w-full rounded-xl bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2" data-testid="invoice-pay-now"><CreditCard size={15} />{paying ? 'Menyiapkan...' : `Bayar Sekarang · ${idr(due)}`}</button>}
          {meta.xendit_enabled && !onPay && <div className="rounded-xl bg-muted px-3 py-2 text-xs text-muted-foreground flex items-center gap-2"><CreditCard size={13} />Tombol "Bayar Sekarang" (Xendit) tampil di sisi pelanggan</div>}
          {meta.bank_accounts?.length > 0 && (
            <div>
              <button type="button" onClick={() => setShowBank((v) => !v)} className="w-full rounded-xl border border-border px-3 py-2 text-sm font-medium hover:bg-muted/60 flex items-center justify-center gap-2" data-testid="invoice-bank-toggle"><Landmark size={14} />Transfer bank manual</button>
              {showBank && <div className="mt-2 space-y-2" data-testid="invoice-bank-list">{meta.bank_accounts.map((b, i) => (
                <div key={i} className="rounded-xl bg-muted/50 p-2.5 text-xs"><div className="font-medium">{b.bank} · {b.account_name}</div><div className="flex items-center gap-2 mt-0.5"><span className="font-mono text-sm tracking-wide">{b.account_number}</span><button type="button" onClick={() => copy(b.account_number)} className="text-primary" aria-label="Salin"><Copy size={12} /></button></div>{b.instructions && <div className="mt-1 text-muted-foreground">{b.instructions}</div>}</div>
              ))}<div className="text-[11px] text-muted-foreground">Nominal transfer: <b>{idr(due)}</b></div></div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const MessageCard = ({ m, onBrowse, onPay, paying, paid }) => {
  if (m.type === 'catalog') return <CatalogCard meta={m.metadata} onBrowse={onBrowse} />;
  if (m.type === 'quotation') return <QuotationCard meta={m.metadata} />;
  if (m.type === 'invoice') return <InvoiceCard meta={m.metadata} onPay={onPay} paying={paying} paid={paid} />;
  return null;
};
